import { type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { useProfileUpdate } from '@/hooks/use-profile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { PhoneInput } from '@/components/ui/phone-input';
import { profileUpdateSchema, type ProfileUpdateFormData } from '@/schemas/profile.schema';
import { AlertCircle, Loader2, Save } from 'lucide-react';

// Mirrors the settings pages: a left title/description column with the
// fields on the right.
function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-4 py-8 first:pt-0">
      <div className="lg:col-span-1">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {description && (
          <p className="text-body-xs text-soft mt-1 leading-relaxed">{description}</p>
        )}
      </div>
      <div className="lg:col-span-2 space-y-4">{children}</div>
    </section>
  );
}

/**
 * Personal profile (name, email, password, account info) rendered in the
 * shared settings layout. Used as the "Profile" tab in both the admin and
 * staff settings pages.
 */
export function ProfileSettingsPanel() {
  const { t, i18n } = useTranslation('profile');
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const updateProfile = useProfileUpdate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ProfileUpdateFormData>({
    resolver: zodResolver(profileUpdateSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
    },
  });

  const onSubmit = async (data: ProfileUpdateFormData) => {
    await updateProfile.mutateAsync(data as { firstName: string; lastName: string; phone?: string });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-soft">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-line">
      {/* Personal Information */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <SettingsSection
          title={t('personalInfo.title')}
          description={t('personalInfo.description')}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t('personalInfo.firstName')} error={errors.firstName?.message}>
              <Input
                id="firstName"
                {...register('firstName')}
                placeholder={t('personalInfo.firstNamePlaceholder')}
              />
            </FormField>

            <FormField label={t('personalInfo.lastName')} error={errors.lastName?.message}>
              <Input
                id="lastName"
                {...register('lastName')}
                placeholder={t('personalInfo.lastNamePlaceholder')}
              />
            </FormField>
          </div>

          <FormField label={t('personalInfo.phone')} error={errors.phone?.message}>
            <PhoneInput
              value={watch('phone') || ''}
              onChange={(value) => setValue('phone', value || '', { shouldDirty: true })}
            />
          </FormField>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isDirty || updateProfile.isPending}
              className="min-h-[44px] w-full sm:w-auto"
            >
              {updateProfile.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('buttons.saving')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('buttons.saveChanges')}
                </>
              )}
            </Button>
          </div>
        </SettingsSection>
      </form>

      {/* Email Address */}
      <SettingsSection title={t('email.title')} description={t('email.description')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <Label>{t('email.currentEmail')}</Label>
            <p className="text-sm font-medium">{user.email}</p>
          </div>
          <Button variant="outline" onClick={() => setLocation('/change-email')}>
            {t('email.changeEmail')}
          </Button>
        </div>

        {user.pendingEmail && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('email.pendingTitle')}</p>
                  <p className="text-sm text-soft mt-1">
                    {t('email.pendingDescription')} <strong>{user.pendingEmail}</strong>
                  </p>
                </div>
                <Badge variant="outline">{t('email.pendingBadge')}</Badge>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </SettingsSection>

      {/* Security - Password */}
      <SettingsSection title={t('security.title')} description={t('security.description')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <Label>{t('security.passwordLabel')}</Label>
            <p className="text-sm text-soft">{t('security.passwordHint')}</p>
          </div>
          <Button variant="outline" onClick={() => setLocation('/change-password')}>
            {t('security.changePassword')}
          </Button>
        </div>
      </SettingsSection>

      {/* Account Information */}
      <SettingsSection title={t('account.title')} description={t('account.description')}>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-soft">{t('account.role')}</Label>
            <Badge variant="secondary">{user.role}</Badge>
          </div>
          <div className="flex justify-between items-center">
            <Label className="text-soft">{t('account.status')}</Label>
            <Badge variant={user.active ? 'default' : 'destructive'}>
              {user.active ? t('account.active') : t('account.inactive')}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <Label className="text-soft">{t('account.memberSince')}</Label>
            <p className="text-sm">
              {new Date(user.createdAt).toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
      </SettingsSection>
    </div>
  );
}
